import { officialApiMultiService, zApiMultiService } from '@/services/api'
import {
  filesData,
  DeleteMessageDTO,
  EditTextMessageDTO,
  EditTextMessageResponse,
  SendMessageDTO,
  SendMessageOfficialAPIDTO,
  SendMessageOfficialAPIResponse,
  SendMessageResponse
} from './types'
import { FileMessageModel } from '@/models/message.model'

const documentExtensions = [
  'doc',
  'docx',
  'odt',
  'rtf',
  'txt',
  'text',
  'md',
  'pdf',
  'pdf',
  'xls',
  'xlsx',
  'ods',
  'ppt',
  'pptx',
  'odp',
  'csv',
  'xml',
  'json',
  'application'
]

export async function sendMessage({ campaignId, token, body, typeFile }: SendMessageDTO) {
  if (typeFile === 'contact' && !body?.messageId) {
    const url = `/instances/campanhaId/${campaignId}/token/${token}/send-contact`
    const contactsMessage = body.contactsMessage
    const response = await zApiMultiService.post<SendMessageResponse>(
      url,
      {
        contactBusinessDescription: contactsMessage?.length ? contactsMessage[0]?.name?.description : undefined,
        contactName: contactsMessage?.length ? contactsMessage[0]?.name?.formatted_name : undefined,
        contactPhone: contactsMessage?.length ? contactsMessage[0]?.phones[0]?.phone : undefined,
        fromMe: true,
        phone: body.phone
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )

    return response.data
  } else if (typeFile !== 'text' && !body?.messageId) {
    const formData = new FormData()
    Object.entries(body).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          if (key === 'filesData') {
            value.forEach((fileData, index) => {
              fileData.fileUrl && formData.append(`${key}[${index}].fileUrl`, fileData.fileUrl)
              fileData.fileUrl && formData.append(`${key}[${index}].mimeType`, fileData.mimeType)
              fileData.file && formData.append(`${key}[${index}].file`, fileData.file)
              formData.append(`${key}[${index}].type`, fileData.type)
            })
          } else {
            formData.append(key, JSON.stringify(value))
          }
        } else {
          formData.append(key, value as any)
        }
      }
    })

    const url = `/instances/campanhaId/${campaignId}/token/${token}/send-multiple-files`
    const response = await zApiMultiService.post<SendMessageResponse>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  } else {
    const url = `/instances/campanhaId/${campaignId}/token/${token}/send-text`
    const response = await zApiMultiService.post<SendMessageResponse>(url, body, {
      headers: {
        'Content-Type': 'application/json'
      }
    })

    return response.data
  }
}

export function buildUnOfficialFilesData(files: Array<Partial<FileMessageModel>>): SendMessageDTO['body']['filesData'] {
  if (!files?.length) return null
  return files.map(file => {
    const [type, ext] = file.type.split('/')
    return {
      ...(file.fileUrl && { fileUrl: file.fileUrl, mimeType: file.mimeType }),
      ...(!file.fileUrl && { file: file as unknown as File }),
      type: documentExtensions.includes(ext) || documentExtensions.includes(type) ? 'document' : type
    }
  })
}

export function buildFilesData(files: Array<Partial<FileMessageModel>>, isRetryMessage: boolean): filesData[] {
  if (
    !files?.length ||
    (files?.some(file => Object.values(file).some(value => !value)) && files?.some(file => !file?.fileUrl))
  )
    return []

  return files.map(file => {
    const splitedType = file?.type ? file.type.split('/') : []
    const [type, ext] = splitedType
    return {
      ...(!isRetryMessage && { file: file as unknown as File }),
      typeFile: documentExtensions.includes(ext) || documentExtensions.includes(type) ? 'document' : type,
      mimeType: file.mimeType || file.type,
      fileName: file.name,
      fileUrl: isRetryMessage ? file.fileUrl : ''
    }
  })
}

export async function deleteMessage({ campaignId, token, messageId, owner, phone }: DeleteMessageDTO) {
  const url = `/instances/campanhaId/${campaignId}/token/${token}/messages`

  const response = await zApiMultiService.delete<any>(url, {
    headers: {
      'Content-Type': 'application/json'
    },
    params: {
      messageId,
      phone,
      owner
    }
  })

  return response.data
}

export async function editTextMessage({
  campaignId,
  token,
  phone,
  bridgeMessageId,
  message
}: EditTextMessageDTO): Promise<EditTextMessageResponse> {
  const url = `/instances/campanhaId/${campaignId}/token/${token}/edit-text-message/${phone}/${bridgeMessageId}`

  const response = await zApiMultiService.post<EditTextMessageResponse>(
    url,
    { message },
    {
      headers: {
        'Content-Type': 'application/json'
      }
    }
  )

  return response.data
}

export async function sendMessageOfficialAPI(dto: SendMessageOfficialAPIDTO): Promise<SendMessageOfficialAPIResponse> {
  let response = null
  const data = { ...dto } as any
  if (dto?.filesData?.length) {
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          if (key === 'filesData') {
            value.forEach((fileData, index) => {
              fileData?.file && formData.append(`${key}[${index}].file`, fileData.file)
              formData.append(`${key}[${index}].fileName`, fileData.fileName)
              formData.append(`${key}[${index}].fileUrl`, fileData.fileUrl)
              formData.append(`${key}[${index}].mimeType`, fileData.mimeType)
              formData.append(`${key}[${index}].typeFile`, fileData.typeFile)
            })
          } else {
            formData.append(key, JSON.stringify(value))
          }
        } else {
          formData.append(key, value as any)
        }
      }
    })

    response = await officialApiMultiService.post<SendMessageOfficialAPIResponse>('/api', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Client-Token':
          process.env.REACT_APP_OFFICIAL_API_KEY || 'bca38b24a804aa37d821d31af00f5598230122c5bbfc4c4ad5ed40e4258f04ca'
      }
    })
  } else {
    response = await officialApiMultiService.post<SendMessageOfficialAPIResponse>('/api', dto, {
      headers: {
        'Content-Type': 'application/json',
        'Client-Token':
          process.env.REACT_APP_OFFICIAL_API_KEY || 'bca38b24a804aa37d821d31af00f5598230122c5bbfc4c4ad5ed40e4258f04ca'
      }
    })
  }

  return response.data
}
